/* eslint-disable no-use-before-define */

export type ColorFormat = "rgb" | "rgba" | "hsl" | "hsla";
export interface ColorObject {
  type: ColorFormat;
  values: number[];
}

const AA_CONTRAST_OK = 4.5;
const AA_CONTRAST_MIN = 2.8; // Lowered from 3 to accomodate white on teal.

export function getContrastText(
  background: string,
  { dark, light }: { dark: string; light: string } = { dark: "#333333", light: "#FFFFFF" },
): string {
  console.assert(background, `Warning: missing background argument in getContrastText(${background}).`);

  const lightContrast = getContrastRatio(background, light);
  const darkContrast = getContrastRatio(background, dark);

  // We favor light text over dark text in most situations
  const contrastText =
    lightContrast >= AA_CONTRAST_OK
      ? light
      : lightContrast >= darkContrast
        ? light
        : lightContrast >= AA_CONTRAST_MIN
          ? light
          : dark;

  if (process.env.NODE_ENV !== "production") {
    const contrast = getContrastRatio(background, contrastText);
    console.assert(
      contrast >= AA_CONTRAST_MIN,
      [
        `Warning: the contrast ratio of ${contrast}:1 for ${contrastText} on ${background}`,
        `falls below the WACG recommended absolute minimum contrast ratio of ${AA_CONTRAST_MIN}:1.`,
        "https://www.w3.org/TR/2008/REC-WCAG20-20081211/#visual-audio-contrast-contrast",
      ].join("\n"),
    );
  }

  return contrastText;
}

/**
 * Returns a number whose value is limited to the given range.
 *
 * @param value The value to be clamped
 * @param min The lower boundary of the output range
 * @param max The upper boundary of the output range
 * @returns A number in the range [min, max]
 */
function clamp(value: number, min = 0, max = 1): number {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

/**
 * Converts a color from CSS hex format to CSS rgb format.
 *
 * @param color - Hex color, i.e. #nnn or #nnnnnn
 * @returns A CSS rgb color string
 */
export function hexToRgb(color: string): string {
  color = color.slice(1);

  const re = new RegExp(`.{1,${color.length / 3}}`, "g");
  let colors: string[] | null = color.match(re);

  if (colors && colors[0].length === 1) {
    colors = colors.map((n) => n + n);
  }

  return colors ? `rgb(${colors.map((n) => parseInt(n, 16)).join(", ")})` : "";
}

function intToHex(int: number): string {
  const hex = int.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}

/**
 * Converts a color from CSS rgb format to CSS hex format.
 *
 * @param color - RGB color, i.e. rgb(n, n, n)
 * @returns A CSS rgb color string, i.e. #nnnnnn
 */
export function rgbToHex(color: string): string {
  // Idempotent
  if (color.indexOf("#") === 0) {
    return color;
  }

  const { values } = decomposeColor(color);
  return `#${values.map((n) => intToHex(n)).join("")}`;
}

/**
 * Converts a color from hsl format to rgb format.
 *
 * @param color - HSL color values
 * @returns rgb color values
 */
export function hslToRgb(color: string): string {
  const decomposed = decomposeColor(color);
  const { values } = decomposed;
  const h = values[0];
  const s = values[1] / 100;
  const l = values[2] / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number, k = (n + h / 30) % 12) => l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

  let type = "rgb";
  const rgb = [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];

  if (decomposed.type === "hsla") {
    type += "a";
    rgb.push(values[3]);
  }

  return recomposeColor({ type: type as ColorFormat, values: rgb });
}

/**
 * Returns an object with the type and values of a color.
 *
 * Note: Does not support rgb % values.
 *
 * @param color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @returns A color object: {type: string, values: number[]}
 */
export function decomposeColor(color: string | ColorObject): ColorObject {
  // Idempotent
  if (typeof color !== "string") {
    return color;
  }

  if (color.charAt(0) === "#") {
    return decomposeColor(hexToRgb(color));
  }

  const marker = color.indexOf("(");
  const type = color.substring(0, marker);

  if (["rgb", "rgba", "hsl", "hsla"].indexOf(type) === -1) {
    throw new Error(
      [
        `Error: unsupported \`${color}\` color.`,
        "We support the following formats: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla().",
      ].join("\n"),
    );
  }

  const values = color
    .substring(marker + 1, color.length - 1)
    .split(",")
    .map((value) => parseFloat(value));

  return { type: type as ColorFormat, values };
}

/**
 * Converts a color object with type and values to a string.
 *
 * @param color - Decomposed color
 * @returns A CSS color string
 */
export function recomposeColor(color: ColorObject): string {
  const { type } = color;
  let values: (string | number)[] = color.values;

  if (type.indexOf("rgb") !== -1) {
    // Only convert the first 3 values to int (i.e. not alpha)
    values = values.map((n, i) => (i < 3 ? parseInt(String(n), 10) : n));
  } else if (type.indexOf("hsl") !== -1) {
    values[1] = `${values[1]}%`;
    values[2] = `${values[2]}%`;
  }

  return `${type}(${values.join(", ")})`;
}

/**
 * Calculates the contrast ratio between two colors.
 *
 * Formula: https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 *
 * @param foreground - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param background - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @returns A contrast ratio value in the range 0 - 21.
 */
export function getContrastRatio(foreground: string, background: string): number {
  const lumA = getLuminance(foreground);
  const lumB = getLuminance(background);
  return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
}

/**
 * The relative brightness of any point in a color space,
 * normalized to 0 for darkest black and 1 for lightest white.
 *
 * Formula: https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 *
 * @param color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @returns The relative brightness of the color in the range 0 - 1
 */
export function getLuminance(color: string): number {
  const decomposed = decomposeColor(color);

  const rgb = (decomposed.type === "hsl" ? decomposeColor(hslToRgb(color)).values : decomposed.values).map((val) => {
    val /= 255; // normalized
    return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
  });

  // Truncate at 3 digits
  return Number((0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]).toFixed(3));
}

/**
 * Darken or lighten a color, depending on its luminance.
 * Light colors are darkened, dark colors are lightened.
 *
 * @param color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param coefficient - multiplier in the range 0 - 1
 * @returns A CSS color string. Hex input values are returned as rgb
 */
export function emphasize(color: string, coefficient = 0.15): string {
  return getLuminance(color) > 0.5 ? darken(color, coefficient) : lighten(color, coefficient);
}

/**
 * Second argument takes restricted values `0.1 | 0.25 | 0.5 | 0.75`
 */
type SteppedValues = 0.1 | 0.25 | 0.5 | 0.75;

/**
 * Set the absolute transparency of a color.
 * Any existing alpha values are overwritten.
 *
 * @param color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param value - value to set the alpha channel to in the range 0 -1
 * @returns A CSS color string. Hex input values are returned as rgb
 */
export function fade(color: string, value: SteppedValues): string {
  const decomposed = decomposeColor(color);
  const clampedValue = clamp(value);

  if (decomposed.type === "rgb" || decomposed.type === "hsl") {
    decomposed.type = `${decomposed.type}a` as ColorFormat;
  }
  decomposed.values[3] = clampedValue;

  return recomposeColor(decomposed);
}

/**
 * Darkens a color.
 *
 * @param color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param coefficient - multiplier in the range 0 - 1
 * @returns A CSS color string. Hex input values are returned as rgb
 */
export function darken(color: string, coefficient: number): string {
  const decomposed = decomposeColor(color);
  const clampedCoefficient = clamp(coefficient);

  if (decomposed.type.indexOf("hsl") !== -1) {
    decomposed.values[2] *= 1 - clampedCoefficient;
  } else if (decomposed.type.indexOf("rgb") !== -1) {
    for (let i = 0; i < 3; i += 1) {
      decomposed.values[i] *= 1 - clampedCoefficient;
    }
  }
  return recomposeColor(decomposed);
}

/**
 * Lightens a color.
 *
 * @param color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param coefficient - multiplier in the range 0 - 1
 * @returns A CSS color string. Hex input values are returned as rgb
 */
export function lighten(color: string, coefficient: number): string {
  const decomposed = decomposeColor(color);
  const clampedCoefficient = clamp(coefficient);

  if (decomposed.type.indexOf("hsl") !== -1) {
    decomposed.values[2] += (100 - decomposed.values[2]) * clampedCoefficient;
  } else if (decomposed.type.indexOf("rgb") !== -1) {
    for (let i = 0; i < 3; i += 1) {
      decomposed.values[i] += (255 - decomposed.values[i]) * clampedCoefficient;
    }
  }

  return recomposeColor(decomposed);
}
